import { useMemo } from 'react';
import { Box, Button, Flex, IconButton, Input, Select, Text } from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import moment from 'moment-timezone';

import { catalogueNote, duplicateIndexes } from './timezoneRows';

/**
 * Where a campaign thinks each recipient is.
 *
 * ★ A CAMPAIGN SENDS AT THE RECIPIENT'S OWN LOCAL HOUR, so the engine has to
 * place every contact in a zone. For most countries the phone number settles
 * it. For a handful — the United States has twenty-nine zones — it does not,
 * and a country-wide guess puts a Californian at New York's hour: 06:00 for a
 * 09:00 campaign, which is the fastest way to lose a number.
 *
 * ★ THE ZONE LIST IS SCOPED TO THE COUNTRY, so an impossible pairing cannot be
 * chosen. The server refuses a zone that does not belong to its country, but a
 * refusal after pressing Save is a worse way to learn it than a list that never
 * offered it.
 *
 * ★ AND THE CITIES WE ALREADY KNOW ARE NAMED. Roughly 150 are built in. An
 * editor that did not say so would have people re-entering Seattle, or quietly
 * overriding a correct built-in without noticing.
 */

/** Country code → "United States", where the browser can tell us. */
const countryName = (() => {
	try {
		const names = new Intl.DisplayNames(['en'], { type: 'region' });
		return (code) => names.of(code) || code;
	} catch (_) {
		// Older engines: the code on its own is still usable.
		return (code) => code;
	}
})();

const zonesFor = (region) => {
	if (!region) return [];
	try {
		return moment.tz.zonesForCountry(String(region).toUpperCase()) ?? [];
	} catch (_) {
		return [];
	}
};

/** "America/Denver (UTC−06:00)" — the offset is what makes the choice mean something. */
const zoneLabel = (zone) => {
	try {
		const offset = moment.tz(zone).format('Z').replace('-', '−');
		return `${zone} (UTC${offset})`;
	} catch (_) {
		return zone;
	}
};

/**
 * Countries that genuinely need a decision.
 *
 * ⚠ NOT simply "more than one zone in the database". Germany lists three and
 * Angola two, but every one of them is on the same clock — listing those would
 * bury the countries that actually differ under a hundred that do not, and the
 * server now settles them on its own.
 *
 * ⚠ AND THE TEST IS A YEAR, NOT AN INSTANT — the same rule the server applies.
 * Ukraine's two zones read alike for half the year and an hour apart for the
 * other half; judging by today alone would drop it from this list for months at
 * a time, hiding the one country most in need of a default.
 */
const SAMPLE_COUNT = 12;
const SAMPLE_STEP_MS = 30 * 24 * 60 * 60 * 1000;

const clockPrint = (zone, from) => {
	const offsets = [];
	for (let i = 0; i < SAMPLE_COUNT; i += 1) {
		try {
			offsets.push(moment.tz(from + i * SAMPLE_STEP_MS, zone).utcOffset());
		} catch (_) {
			offsets.push(null);
		}
	}
	return offsets.join(',');
};

const useAmbiguousCountries = () =>
	useMemo(() => {
		const from = Date.now();
		return moment.tz
			.countries()
			.map((code) => {
				const zones = zonesFor(code);
				const clocks = new Set(zones.map((zone) => clockPrint(zone, from)));
				return { code, zones, clocks: clocks.size };
			})
			.filter((c) => c.clocks > 1)
			.sort((a, b) => b.clocks - a.clocks || countryName(a.code).localeCompare(countryName(b.code)));
	}, []);

/** A row's country picker, which also has to keep showing a country already saved. */
function CountrySelect({ value, options, onChange, ui, placeholder = 'Country' }) {
	const known = options.some((c) => c.code === value);
	return (
		<Select
			size='sm'
			maxW='210px'
			value={value ?? ''}
			onChange={(e) => onChange(e.target.value)}
			bg={ui.inputBg}
			borderColor={ui.border}
			color={ui.textPrimary}
		>
			<option value=''>{placeholder}</option>
			{/* A country saved earlier stays selectable even if it is not in the
			    ambiguous list — otherwise editing it would silently drop it. */}
			{!known && value && <option value={value}>{countryName(value)}</option>}
			{options.map((c) => (
				<option key={c.code} value={c.code}>
					{countryName(c.code)} — {c.clocks} clocks
				</option>
			))}
		</Select>
	);
}

function ZoneSelect({ region, value, onChange, ui, maxW = '260px' }) {
	const zones = zonesFor(region);
	const known = zones.includes(value);
	return (
		<Select
			size='sm'
			maxW={maxW}
			value={value ?? ''}
			onChange={(e) => onChange(e.target.value)}
			bg={ui.inputBg}
			borderColor={ui.border}
			color={ui.textPrimary}
			isDisabled={!region}
		>
			<option value=''>{region ? 'Choose a time zone' : 'Choose a country first'}</option>
			{/* A stored zone the country no longer lists is still shown, so it can
			    be seen and corrected rather than silently replaced on save. */}
			{!known && value && <option value={value}>{value}</option>}
			{zones.map((zone) => (
				<option key={zone} value={zone}>
					{zoneLabel(zone)}
				</option>
			))}
		</Select>
	);
}

export default function TimezoneRules({ tzRows, setTzRows, cityRows, setCityRows, catalogue, ui }) {
	const countries = useAmbiguousCountries();
	const duplicates = duplicateIndexes(cityRows);

	const knownCityCount = useMemo(
		() => Object.values(catalogue ?? {}).reduce((total, cities) => total + Object.keys(cities ?? {}).length, 0),
		[catalogue],
	);

	const takenCountries = new Set(tzRows.map((r) => r.region));
	const addableCountries = countries.filter((c) => !takenCountries.has(c.code));

	const setTz = (index, patch) => setTzRows(tzRows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
	const setCity = (index, patch) => setCityRows(cityRows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

	return (
		<Box>
			{/* ── Country defaults ───────────────────────────────────────── */}
			<Text fontSize='sm' color={ui.textPrimary} fontWeight='medium' mb={1}>
				When we only know the country
			</Text>
			<Text fontSize='xs' color={ui.textMuted} mb={3} maxW='78ch'>
				Most countries have a single time zone and the phone number is enough. For the ones below, assume this
				zone unless the contact’s city tells us otherwise.
			</Text>

			{tzRows.map((row, index) => (
				// eslint-disable-next-line react/no-array-index-key
				<Flex key={`tz-${index}`} gap={2} align='center' mb={2} wrap='wrap'>
					<CountrySelect
						value={row.region}
						options={addableCountries}
						onChange={(region) => setTz(index, { region, zone: '' })}
						ui={ui}
					/>
					<ZoneSelect region={row.region} value={row.zone} onChange={(zone) => setTz(index, { zone })} ui={ui} />
					<IconButton
						size='sm'
						variant='ghost'
						aria-label={`Remove the default for ${row.region || 'this country'}`}
						icon={<FiTrash2 />}
						onClick={() => setTzRows(tzRows.filter((_, i) => i !== index))}
					/>
				</Flex>
			))}

			<Button
				size='xs'
				variant='outline'
				leftIcon={<FiPlus />}
				mt={1}
				onClick={() => setTzRows([...tzRows, { region: '', zone: '' }])}
			>
				Add a country
			</Button>

			{/* ── City exceptions ────────────────────────────────────────── */}
			<Text fontSize='sm' color={ui.textPrimary} fontWeight='medium' mt={6} mb={1}>
				Cities that need their own zone
			</Text>
			<Text fontSize='xs' color={ui.textMuted} mb={3} maxW='78ch'>
				{knownCityCount > 0
					? `About ${knownCityCount} major cities are already recognised in those countries — Seattle, Chicago, Manaus and the rest — and a contact whose city we know is placed by it automatically. Add a row only for a city we do not know, or one your data spells differently.`
					: 'Add a row for a city whose zone differs from its country default.'}
			</Text>

			{!cityRows.length && (
				<Text fontSize='xs' color={ui.textMuted} mb={2}>
					No exceptions. Nothing is wrong — most workspaces need none.
				</Text>
			)}

			{cityRows.map((row, index) => {
				const note = catalogueNote(row, catalogue);
				const isDuplicate = duplicates.has(index);
				return (
					// eslint-disable-next-line react/no-array-index-key
					<Box key={`city-${index}`} mb={2}>
						<Flex gap={2} align='center' wrap='wrap'>
							<CountrySelect
								value={row.region}
								options={countries}
								onChange={(region) => setCity(index, { region, zone: '' })}
								ui={ui}
							/>
							<Input
								size='sm'
								maxW='190px'
								placeholder='City'
								value={row.city ?? ''}
								onChange={(e) => setCity(index, { city: e.target.value })}
								bg={ui.inputBg}
								borderColor={ui.border}
								color={ui.textPrimary}
							/>
							<ZoneSelect
								region={row.region}
								value={row.zone}
								onChange={(zone) => setCity(index, { zone })}
								ui={ui}
								maxW='240px'
							/>
							<IconButton
								size='sm'
								variant='ghost'
								aria-label={`Remove ${row.city || 'this city'}`}
								icon={<FiTrash2 />}
								onClick={() => setCityRows(cityRows.filter((_, i) => i !== index))}
							/>
						</Flex>

						{isDuplicate && (
							<Text fontSize='xs' color={ui.warning} mt={1}>
								A row above already sets this city. This one wins — remove whichever you do not want.
							</Text>
						)}
						{!isDuplicate && note && (
							<Text fontSize='xs' color={note.tone === 'warning' ? ui.warning : ui.textMuted} mt={1}>
								{note.text}
							</Text>
						)}
					</Box>
				);
			})}

			<Button
				size='xs'
				variant='outline'
				leftIcon={<FiPlus />}
				mt={1}
				onClick={() => setCityRows([...cityRows, { region: '', city: '', zone: '' }])}
			>
				Add a city
			</Button>
		</Box>
	);
}
